import { ref, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import ProjectService from "../services/projectService.js";
import { useProjectsList } from "./useProjectsList.js";

// Module-level so re-opening the panel doesn't lose scroll/pagination state
//
// Which project's docs the panel is showing. Normally tracks the route
// (v2-project/v2-document's :projectId, see routeProjectId below), but can be
// temporarily overridden via viewProject() to show a different project's docs
// WITHOUT navigating — used by the switcher while pinned to a document's
// annotation view, so picking another project doesn't leave the document;
// only clicking one of its files does. A genuine route change (e.g. actually
// opening a file, or leaving the project entirely) always resyncs this to
// the route via the watcher in activate(), so an override never gets stuck.
const currentProjectId = ref(null);
const currentProject = ref(null);
const docs = ref([]);
const isLoadingDocs = ref(false);
const isLoadingMoreDocs = ref(false);
const hasMoreDocs = ref(true);
let docsCursor = null;
let loadedForProjectId = null;
let hasWatcherStarted = false;
let lastDocsSortBy = "DATE_ADDED";
let lastDocsSortDirection = "DESC";
let refreshPromise = null;

const projectService = new ProjectService();

// Favoriting/renaming/deleting a doc happens from Project.vue's own grid (a
// separate fetch/cache from this panel's docs list), so none of it would
// otherwise show up here until the panel reloaded — Project.vue and
// useDocumentUpload.js call these directly to keep the panel in sync.
// Module-level (not inside the composable factory below) since they only
// touch the module state above, and useDocumentUpload.js calls refreshDocs()
// from async upload-completion callbacks, outside any component's setup —
// unlike useProjectsPanel() itself, they don't need useRoute()/useRouter().
function setDocFavorite(docId, isFavorite) {
  const d = docs.value.find((d) => d.id === docId);
  if (d) d.isFavorite = isFavorite;
}

function renameDoc(docId, name) {
  const d = docs.value.find((d) => d.id === docId);
  if (d) d.name = name;
}

function removeDoc(docId) {
  docs.value = docs.value.filter((d) => d.id !== docId);
}

// Silently re-fetch enough pages to at least cover what was already loaded
// (so a newly uploaded doc shows up) — mirrors useProjectDocs.js's refresh.
// No-ops if the panel isn't currently showing this project (either closed,
// or showing a different one), same guard as useProjectDocs.refresh.
function refreshDocs(projectId) {
  if (projectId !== currentProjectId.value) return Promise.resolve();
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    const targetCount = Math.max(docs.value.length, 30);
    let items = [];
    let pageCursor = null;
    let more = true;
    while (items.length < targetCount && more) {
      const res = await projectService.getDocuments({
        projectId, pageSize: 30, endCursor: pageCursor,
        sortBy: lastDocsSortBy, sortDirection: lastDocsSortDirection,
      });
      items = items.concat(res.documents ?? []);
      pageCursor = res.endCursor;
      more = res.hasNextPage;
    }
    docs.value = items;
    docsCursor = pageCursor;
    hasMoreDocs.value = more;
  })().finally(() => { refreshPromise = null; });
  return refreshPromise;
}

export function useProjectsPanel() {
  const route = useRoute();
  const router = useRouter();
  const { projects, isLoading: isLoadingProjects, isLoadingMore: isLoadingMoreProjects, hasMore: hasMoreProjects, ensureLoaded: ensureProjectsLoaded, loadMore: loadMoreProjects, resort: resortProjects } = useProjectsList();

  // Viewing a specific project (or a document inside one) — panel shows that project's docs.
  // Otherwise (projects list, home, docs, etc.) — panel shows the all-projects browsing list.
  const routeProjectId = computed(() => {
    const inProjectContext = route.name === "v2-project" || route.name === "v2-document";
    return inProjectContext ? route.params.projectId ?? null : null;
  });

  // Download-only files (isAnnotatable: false) get their own section at the
  // bottom, starred or not — same split as Project.vue's grid.
  const annotatableDocs = computed(() => docs.value.filter((d) => d.isAnnotatable !== false));
  const starredDocs = computed(() => annotatableDocs.value.filter((d) => d.isFavorite));
  const otherDocs = computed(() => annotatableDocs.value.filter((d) => !d.isFavorite));
  const downloadOnlyDocs = computed(() => docs.value.filter((d) => d.isAnnotatable === false));

  async function loadProjectInfo(projectId) {
    const fromList = projects.value.find((p) => p.id === projectId);
    if (fromList) currentProject.value = fromList;
    const fresh = await projectService.getProject({ projectId });
    if (fresh?.id) currentProject.value = fresh;
  }

  async function loadDocsFirstPage(projectId) {
    if (loadedForProjectId === projectId) return; // already loaded for this project
    loadedForProjectId = projectId;
    docs.value = [];
    docsCursor = null;
    hasMoreDocs.value = true;
    isLoadingDocs.value = true;
    await loadMoreDocs(projectId);
    isLoadingDocs.value = false;
  }

  async function loadMoreDocs(projectId) {
    const pid = projectId ?? currentProjectId.value;
    if (!pid || !hasMoreDocs.value || isLoadingMoreDocs.value) return;
    isLoadingMoreDocs.value = true;
    try {
      const res = await projectService.getDocuments({
        projectId: pid,
        pageSize: 30,
        endCursor: docsCursor,
        sortBy: lastDocsSortBy,
        sortDirection: lastDocsSortDirection,
      });
      docs.value.push(...(res.documents ?? []));
      docsCursor = res.endCursor;
      hasMoreDocs.value = res.hasNextPage;
    } finally {
      isLoadingMoreDocs.value = false;
    }
  }

  // Sort is applied server-side, so changing it re-fetches this project's
  // docs from the first page.
  async function resortDocs(sortBy, sortDirection) {
    lastDocsSortBy = sortBy;
    lastDocsSortDirection = sortDirection;
    const projectId = currentProjectId.value;
    if (!projectId) return;
    loadedForProjectId = null; // force loadDocsFirstPage to actually reload
    await loadDocsFirstPage(projectId);
  }

  // Lazily start reacting to route changes only once the panel has actually
  // been opened — avoids fetching anything while it's just mounted-but-hidden.
  function activate() {
    if (hasWatcherStarted) return;
    hasWatcherStarted = true;
    watch(
      routeProjectId,
      (projectId) => {
        // A genuine route change always wins over any viewProject() override —
        // e.g. actually opening a file resyncs the panel to that file's project.
        currentProjectId.value = projectId;
        // The project switcher dropdown needs the all-projects list whether
        // or not we're currently inside a project (e.g. landing directly on
        // a project route via refresh/deep link must still warm it) — a
        // no-op after the first call thanks to ensureProjectsLoaded's own guard.
        ensureProjectsLoaded();
        if (projectId) {
          loadProjectInfo(projectId);
          loadDocsFirstPage(projectId);
        } else {
          // Leaving project/document context (e.g. back to "All Projects") —
          // clear the stale project so its (possibly singleton) header/data
          // doesn't leak into the all-projects browsing view.
          currentProject.value = null;
        }
      },
      { immediate: true }
    );
  }

  function openProject(id) {
    router.push({ name: "v2-project", params: { projectId: id } });
  }

  // Show a different project's docs in the panel WITHOUT navigating — see the
  // comment on currentProjectId above for why this exists.
  async function viewProject(id) {
    currentProjectId.value = id;
    await loadProjectInfo(id);
    await loadDocsFirstPage(id);
  }

  return {
    currentProjectId,
    currentProject,
    // "Not inside a project" — browse all projects
    projects,
    isLoadingProjects,
    isLoadingMoreProjects,
    hasMoreProjects,
    loadMoreProjects,
    ensureProjectsLoaded,
    resortProjects,
    openProject,
    viewProject,
    // "Inside a project" — that project's documents
    starredDocs,
    otherDocs,
    downloadOnlyDocs,
    isLoadingDocs,
    isLoadingMoreDocs,
    hasMoreDocs,
    loadMoreDocs,
    resortDocs,
    setDocFavorite,
    renameDoc,
    removeDoc,
    refreshDocs,
    activate,
  };
}
